// ============================================================================
// APP.JSX — ReferralGate Main Application (RBAC v2 + Sector-based RouteGuard)
//
// Auth bootstrap flow (fail-fast):
//   1. onAuthChange fires → setRawAuthUser
//   2. If profile returned by auth adapter → use it
//   3. If auth user exists but no profile → call ensureUserProfile()
//      a. Profile created (pending) → show AccessPendingView
//      b. PROFILE_PERMISSION_DENIED → show ProfileErrorView + Retry button
//   4. role === "pending" OR tenantId === null (non-platform_admin) → AccessPendingView
//   5. Normal app render
//
// RouteGuard:
//   - Every route has a sector (ROUTE_TO_SECTOR map)
//   - canAccessSector(role, sector) gates entry
//   - AccessDenied shown immediately — NO Firestore reads attempted for blocked pages
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNotYetBadgeCount } from './hooks/useNotYetBadgeCount.js';
import { useReferralPingsUnread } from './hooks/useReferralPingsUnread.js';
import { useQueuePingsUnread } from './hooks/useQueuePingsUnread.js';
import {
  Shield, Home, Building, Users, Package, BarChart3,
  LogOut, Lock, Activity, FileText, AlertTriangle,
  Menu, ChevronLeft, ChevronRight, X, Clock,
  FileSearch, List, Calendar, CheckCircle2, RefreshCw,
  RotateCcw, UserPlus, Mail, LineChart,
  ClipboardList, History, Stethoscope, BookOpen, Bell, Settings, Plug,
} from 'lucide-react';

// Auth Context
import { AuthContext } from './auth/AuthContext.jsx';

// Theme primitives (semantic, token-driven)
import { RGPageShell } from './components/rg/RGPrimitives.jsx';

// Core
import {
  getTenants, getTenant, getStoreMode,
  ROLES, ROLE_DISPLAY, DEFAULT_LANDING, SECTORS, ROUTE_TO_SECTOR,
  defaultRouteForRole,
  isPlatformAdmin, isTenantAdmin, isOpsManager,
  isGpEmployee, isIntakeCoordinator, isTriageNurse,
  isScheduler, isSpecialist, isSpecialistAdmin, isPending,
  isClinician, canAccessSector, canAccessRoute, getAllowedRoutes,
  signIn, signUp, logOut, onAuthChange,
} from './core/index.js';

// Auth bootstrap
import { ensureUserProfile } from './core/authBootstrap.js';
import { selfHealIsActiveCF } from './core/functions.js';

// SMART Build 2L — launch continuation (deep-link capture + pending intent)
import { parseLaunchSessionIdFromSearch } from './core/smartLaunchContinuationGate.js';
import { setSmartContinueIntent, peekSmartContinueIntent } from './core/smartContinueIntent.js';

// Views — existing
import LoginView from './LoginView.jsx';
import PlatformDashboard from './views/PlatformDashboard.jsx';
import TenantsView from './views/TenantsView.jsx';
import UsersView from './views/UsersView.jsx';
import PackManagerView from './views/PackManagerView.jsx';
import PhiPolicySettingsView from './views/PhiPolicySettingsView.jsx';
import OpsView from './views/OpsView.jsx';
import GateView from './views/GateView.jsx';
import TenantAnalyticsDashboard from './views/TenantAnalyticsDashboard.jsx';
import SpecialtyAnalyticsView from './views/SpecialtyAnalyticsView.jsx';
import MySubmissionsView from './views/MySubmissionsView.jsx';
import NotYetInboxView from './views/NotYetInboxView.jsx';
import TenantAdminBusinessMetrics from './views/TenantAdminBusinessMetrics.jsx';
import SpecialistBusinessMetrics from './views/SpecialistBusinessMetrics.jsx';
import ClinicianDashboard from './views/ClinicianDashboard.jsx';
import AuditLogsView from './views/AuditLogsView.jsx';
import ReferralPingsView from './views/ReferralPingsView.jsx';
import SettingsView from './views/SettingsView.jsx';
import TenantImpactSettingsView from './views/TenantImpactSettingsView.jsx';
import EhrIntegrationSettingsView from './views/EhrIntegrationSettingsView.jsx';
import PlatformOpsCommandCenter from './views/PlatformOpsCommandCenter.jsx';
import PacketInboxView from './views/PacketInboxView.jsx';
import SmartLaunchContinueView from './views/SmartLaunchContinueView.jsx';

// Views — new
import AccessPendingView from './views/AccessPendingView.jsx';
import InvitesView from './views/InvitesView.jsx';
import WorkqueueListView from './views/WorkqueueListView.jsx';
import SchedulingView from './views/SchedulingView.jsx';
import ClosedLoopView from './views/ClosedLoopView.jsx';
import TriageView from './views/TriageView.jsx';
import TriageQueueView from './views/TriageQueueView.jsx';
import MyTriageHistoryView from './views/MyTriageHistoryView.jsx';
import SpecialistWorklistView from './views/SpecialistWorklistView.jsx';
import SpecialistAdminWorkplaceView from './views/SpecialistAdminWorkplaceView.jsx';
import AcceptInviteView from './views/AcceptInviteView.jsx';
import SpecialistPackBuilderView from './views/SpecialistPackBuilderView.jsx';

// Views — Analytics (Stage 7)
import OpsHomeView from './views/Analytics/OpsHomeView.jsx';
import PerformanceView from './views/Analytics/PerformanceView.jsx';
import DeepDiveView from './views/Analytics/DeepDiveView.jsx';
import FlowQualityFunnelView from './views/Analytics/FlowQualityFunnelView.jsx';
import SchedulerHomeView from './views/Analytics/SchedulerHomeView.jsx';
import TriageQueueHealthView from './views/Analytics/TriageQueueHealthView.jsx';
import OpsManagerDashboardView from './views/Analytics/OpsManagerDashboardView.jsx';
import SpecialistQueueHealthView from './views/Analytics/SpecialistQueueHealthView.jsx';
import SpecialistAdminQueueHealthView from './views/Analytics/SpecialistAdminQueueHealthView.jsx';

// Attachment viewer (route-based PDF viewer)
import AttachmentPdfViewer, { consumeViewerReturnContext } from './components/AttachmentPdfViewer.jsx';

// Analytics Auth Guard (Stage 9) — view-level permission gate
import AnalyticsGuard from './core/guards/AnalyticsGuard.jsx';

// Learning — Role Orientation + Guided Modules
import { getOrientationCard, isRoleLearningConfigured } from './learning/roleLearningMaps.js';
import { useRoleLearningState } from './learning/useRoleLearningState.js';
import RoleOrientationCard from './learning/RoleOrientationCard.jsx';
import { getLearningModuleForRoute } from './learning/getLearningModuleForRoute.js';
import GuidedLearningPanel from './learning/GuidedLearningPanel.jsx';

const isFirebase = () => getStoreMode() === 'firebase';
const SIDEBAR_COLLAPSED_KEY = 'referralgate_sidebar_collapsed';

// ============================================================================
// HOOKS
// ============================================================================

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

// ============================================================================
// STATIC UI COMPONENTS
// ============================================================================

// App shell canvas — themed via RGPageShell (flat cool background in Light,
// depth gradient in Dark, handled by the .rg-app-shell token class).
const AnimatedBackground = () => <RGPageShell />;

/** AccessDenied — shown when sector check fails. Does NOT trigger Firestore reads. */
const AccessDenied = ({ role, route }) => (
  <div className="flex items-center justify-center h-full min-h-[60vh]">
    <div className="text-center p-8 max-w-md">
      <div className="w-20 h-20 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-6">
        <Lock className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-2xl font-semibold text-white mb-2">Access Denied</h2>
      <p className="text-slate-400">
        {role && route
          ? `Your role (${ROLE_DISPLAY[role]?.name ?? role}) cannot access this page.`
          : "You don't have permission to view this page."}
      </p>
    </div>
  </div>
);

/** ComingSoon — placeholder for unbuilt views. */
const ComingSoon = ({ title }) => (
  <div className="flex items-center justify-center h-full min-h-[60vh]">
    <div className="text-center p-8 max-w-md">
      <div className="w-20 h-20 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-6">
        <Activity className="w-10 h-10 text-slate-500" />
      </div>
      <h2 className="text-2xl font-semibold text-white mb-2">{title}</h2>
      <p className="text-slate-400">This feature is coming soon.</p>
    </div>
  </div>
);

/** Loading spinner for auth bootstrap. */
const LoadingScreen = ({ message = 'Loading…' }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 animate-pulse">
        <Shield className="w-8 h-8 text-white" />
      </div>
      <p className="text-slate-400">{message}</p>
    </div>
  </div>
);

/**
 * ProfileErrorView — blocking error when /users/{uid} is unreadable.
 * Provides a Retry button that re-attempts ensureUserProfile().
 */
const ProfileErrorView = ({ onLogout, onRetry, retrying }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
    <div className="relative z-10 text-center p-8 max-w-lg w-full mx-4">
      <div className="w-24 h-24 rounded-3xl bg-red-500/20 flex items-center justify-center mx-auto mb-8">
        <AlertTriangle className="w-12 h-12 text-red-400" />
      </div>
      <h1 className="text-3xl font-semibold text-white mb-3">
        Cannot Load User Profile
      </h1>
      <p className="text-slate-400 text-base leading-relaxed mb-8">
        Your user profile could not be read (permissions error or missing user doc).
        Contact your administrator, or retry if you just signed up.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={onRetry}
          disabled={retrying}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium transition-all"
        >
          <RotateCcw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-medium transition-all"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// ROLE → COLOUR
// ============================================================================

const getRoleColor = (role) => ({
  platform_admin:     'amber',
  tenant_admin:       'violet',
  ops_manager:        'cyan',
  specialist:         'blue',
  specialist_admin:   'sky',
  intake_coordinator: 'teal',
  triage_nurse:       'orange',
  scheduler:          'indigo',
  gp_employee:        'emerald',
  pending:            'slate',
}[role] ?? 'slate');

// ============================================================================
// SIDEBAR
// ============================================================================

const getNavItems = (role) => {
  if (isPlatformAdmin(role)) return [
    { id: 'dashboard',       label: 'Dashboard',        icon: Home },
    { id: 'tenants',         label: 'Tenants',          icon: Building },
    { id: 'packs',           label: 'Pack Manager',     icon: Package },
    { id: 'users',           label: 'Users',            icon: Users },
    { id: 'impact-settings', label: 'Impact Settings',  icon: Activity },
    { id: 'ehr-integration', label: 'EHR Integration',  icon: Plug },
    { id: 'ops-dashboard',   label: 'Ops Command Center', icon: BarChart3 },
  ];
  if (isTenantAdmin(role)) return [
    { id: 'ops-dashboard',   label: 'Ops Command Center', icon: BarChart3 },
    { id: 'users',           label: 'Staff',            icon: Users },
    { id: 'invites',         label: 'Invites',          icon: Mail },
    { id: 'referral-pings',  label: 'Referral Pings',   icon: Bell },
    { id: 'packet-inbox',    label: 'Packet Inbox',     icon: ClipboardList },
    { id: 'impact-settings', label: 'Impact Settings',  icon: Activity },
    { id: 'ehr-integration', label: 'EHR Integration',  icon: Plug },
    { id: 'audit-logs',      label: 'Audit Logs',       icon: FileSearch },
  ];
  if (isOpsManager(role)) return [
    { id: 'ops-dashboard',   label: 'Ops Command Center', icon: BarChart3 },
    { id: 'referral-pings',  label: 'Referral Pings',   icon: Bell },
    { id: 'packet-inbox',    label: 'Packet Inbox',     icon: ClipboardList },
    { id: 'impact-settings', label: 'Impact Settings',  icon: Activity },
  ];
  if (isGpEmployee(role)) return [
    { id: 'packet-inbox',   label: 'Packet Inbox',      icon: ClipboardList },
    { id: 'not-yet-inbox',  label: 'Not Yet Inbox',     icon: Clock },
    { id: 'referral-pings', label: 'Referral Pings',    icon: Bell },
    { id: 'my-submissions', label: 'My Submissions',    icon: FileText },
  ];
  if (isIntakeCoordinator(role)) return [
    { id: 'workqueue',     label: 'Workqueue',          icon: List },
    { id: 'packet-inbox',  label: 'Packet Inbox',       icon: ClipboardList },
    { id: 'not-yet-inbox', label: 'Needs Info',         icon: Clock },
    { id: 'referral-pings', label: 'Referral Pings',    icon: Bell },
    { id: 'ops-home',      label: 'Queue Health',       icon: Activity },
  ];
  if (isTriageNurse(role)) return [
    { id: 'triage-queue',          label: 'Triage Queue',      icon: ClipboardList },
    { id: 'triage-queue-health',   label: 'Queue Health',      icon: Activity },
    { id: 'referral-pings',        label: 'Referral Pings',    icon: Bell },
  ];
  if (isScheduler(role)) return [
    { id: 'scheduling',      label: 'Scheduling',      icon: Calendar },
    { id: 'scheduler-home',  label: 'Queue Health',  icon: Activity },
    { id: 'referral-pings',  label: 'Referral Pings',  icon: Bell },
  ];
  if (isSpecialist(role)) return [
    { id: 'specialist-worklist',     label: 'My Worklist',       icon: Stethoscope },
    { id: 'specialist-queue-health', label: 'Queue Health',      icon: Activity },
    { id: 'packs',                   label: 'Pack Builder',      icon: Package },
    { id: 'referral-pings',          label: 'Referral Pings',    icon: Bell },
  ];
  if (isSpecialistAdmin(role)) return [
    { id: 'specialist-admin-workplace',    label: 'My Workplace', icon: Stethoscope },
    { id: 'specialist-admin-queue-health', label: 'Queue Health', icon: Activity },
    { id: 'referral-pings',                label: 'Referral Pings', icon: Bell },
  ];
  return [];
};

const Sidebar = ({
  role, currentView, onNavigate, onLogout,
  user, tenant, isCollapsed, onToggleCollapse,
  isMobile, isMobileOpen, onCloseMobile,
  activeFocusTarget,
}) => {
  const roleColor  = getRoleColor(role);
  const roleDisplay = ROLE_DISPLAY[role] ?? { name: role };
  const navItems   = getNavItems(role);

  // Live badge count — reuses the exact same Firestore filter as NotYetInboxView
  const uid        = user?.uid || user?.id || null;
  const tenantId   = tenant?.id || tenant?.tenantId || null;
  const notYetCount = useNotYetBadgeCount({ role, tenantId, uid });
  const referralPingsUnread = useReferralPingsUnread({ tenantId, uid });
  const queuePingsUnread = useQueuePingsUnread({ tenantId, uid });
  // Combined Referral Pings attention badge = unread referral pings + unread queue pings.
  const pingsAttention = referralPingsUnread + queuePingsUnread;
  // Map nav item id → badge count (extend here for additional badges in future)
  const badgeCounts = {
    ...(notYetCount > 0 ? { 'not-yet-inbox': notYetCount } : {}),
    ...(pingsAttention > 0 ? { 'referral-pings': pingsAttention } : {}),
  };

  if (isMobile && !isMobileOpen) return null;

  const body = (
    <div className={`
      ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'fixed inset-y-0 left-0 z-40'}
      ${isCollapsed && !isMobile ? 'w-16' : 'w-64'}
      bg-slate-900/95 backdrop-blur-sm border-r border-slate-800/50
      flex flex-col transition-all duration-300 ease-in-out h-screen
    `}>
      {/* Header */}
      <div className={`p-4 border-b border-slate-800/50 ${isCollapsed && !isMobile ? 'px-2' : ''}`}>
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-3 ${isCollapsed && !isMobile ? 'justify-center w-full' : ''}`}>
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${roleColor}-400 to-${roleColor}-600 flex items-center justify-center flex-shrink-0`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            {(!isCollapsed || isMobile) && (
              <div className="overflow-hidden">
                <h1 className="font-semibold text-white whitespace-nowrap">ReferralGate</h1>
                <p className={`text-xs text-${roleColor}-400 whitespace-nowrap`}>
                  {isPlatformAdmin(role) ? 'Platform' : (tenant?.name ?? 'No Tenant')}
                </p>
              </div>
            )}
          </div>
          {isMobile ? (
            <button onClick={onCloseMobile} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={onToggleCollapse} className={`p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white ${isCollapsed ? 'mx-auto mt-2' : ''}`}>
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className={`flex-1 space-y-1 ${isCollapsed && !isMobile ? 'px-1 p-2' : 'p-4'} ${activeFocusTarget === 'intake_sidebar' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}`} data-learning-target="intake_sidebar">
        {navItems.map((item) => {
          const badge = badgeCounts[item.id] ?? 0;
          const badgeLabel = badge > 99 ? '99+' : String(badge);
          const NAV_FOCUS_MAP = { 'not-yet-inbox': 'not_yet_nav_item', 'my-submissions': 'my_submissions_nav_item' };
          const LEARNING_TARGET_MAP = { 'workqueue': 'workqueue_nav', 'not-yet-inbox': 'needs_info_nav', 'ops-home': 'queue_health_nav' };
          const navFocusId = NAV_FOCUS_MAP[item.id] || null;
          const learningTargetId = LEARNING_TARGET_MAP[item.id] || null;
          const isNavHighlighted = navFocusId && activeFocusTarget === navFocusId;
          const isLearningHighlighted = learningTargetId && activeFocusTarget === learningTargetId;
          return (
            <button
              key={item.id}
              onClick={() => { onNavigate(item.id); if (isMobile) onCloseMobile(); }}
              data-focus-target={navFocusId || undefined}
              data-learning-target={learningTargetId || undefined}
              className={`
                w-full flex items-center gap-3 rounded-xl transition-all duration-200
                ${isCollapsed && !isMobile ? 'justify-center px-2 py-3' : 'px-4 py-3'}
                ${currentView === item.id
                  ? `bg-${roleColor}-500/20 text-${roleColor}-400 border border-${roleColor}-500/30`
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}
                ${isNavHighlighted || isLearningHighlighted ? 'learning-focus-highlight' : ''}
              `}
              title={isCollapsed && !isMobile ? item.label : undefined}
            >
              {/* Icon — badge overlays icon when sidebar is collapsed */}
              <div className="relative flex-shrink-0">
                <item.icon className="w-5 h-5" />
                {isCollapsed && !isMobile && badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 leading-none pointer-events-none">
                    {badgeLabel}
                  </span>
                )}
              </div>
              {/* Label + inline badge when sidebar is expanded */}
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="font-medium whitespace-nowrap flex-1 text-left">{item.label}</span>
                  {badge > 0 && (
                    <span className="bg-amber-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 leading-none flex-shrink-0">
                      {badgeLabel}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* User footer */}
      <div className={`p-4 border-t border-slate-800/50 ${isCollapsed && !isMobile ? 'p-2' : ''}`}>
        {(!isCollapsed || isMobile) ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-white">
                  {(user?.displayName ?? user?.name ?? 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.displayName ?? user?.name ?? 'User'}
                </p>
                <p className={`text-xs text-${roleColor}-400`}>{roleDisplay.name}</p>
              </div>
            </div>
            <button
              onClick={() => { onNavigate('settings'); if (isMobile) onCloseMobile(); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <Settings className="w-4 h-4" /> Settings
            </button>
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {(user?.displayName ?? user?.name ?? 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <button onClick={() => onNavigate('settings')} className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Settings">
              <Settings className="w-4 h-4" />
            </button>
            <button onClick={onLogout} className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" title="Sign Out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 z-40" onClick={onCloseMobile} />
        {body}
      </>
    );
  }
  return body;
};

const MobileHeader = ({ onOpenSidebar, role }) => {
  const roleColor = getRoleColor(role);
  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
      <div className="flex items-center gap-3">
        <button onClick={onOpenSidebar} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
          <Menu className="w-5 h-5" />
        </button>
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-${roleColor}-400 to-${roleColor}-600 flex items-center justify-center`}>
          <Shield className="w-4 h-4 text-white" />
        </div>
        <h1 className="font-semibold text-white">ReferralGate</h1>
      </div>
    </div>
  );
};

// ============================================================================
// ROUTE GUARD
// Checks sector access BEFORE rendering page content.
// If sector is blocked, shows AccessDenied — no Firestore reads occur.
// ============================================================================

const RouteGuard = ({ role, route, children }) => {
  const sector = ROUTE_TO_SECTOR[route];

  // 'dashboard' is role-specific and always allowed for the role's landing
  // (each role has it in getAllowedRoutes if it's meaningful for them).
  if (route === 'dashboard') {
    return canAccessRoute(role, 'dashboard') ? children : <AccessDenied role={role} route={route} />;
  }

  // Sector-gated routes
  if (sector && !canAccessSector(role, sector)) {
    return <AccessDenied role={role} route={route} />;
  }

  // Double-check against explicit route allowlist
  if (!canAccessRoute(role, route)) {
    return <AccessDenied role={role} route={route} />;
  }

  return children;
};

// ============================================================================
// VIEW RENDERER (role × route → React element)
// RouteGuard wraps each case so Firestore reads in child components are
// never reached for blocked routes.
// ============================================================================

const renderView = (role, view, user, handleNavigate, { autoOpenReferralId, navState, activeFocusTarget, onModalContext, onLearningContext, tenant, onLogout, smartLaunchSessionId } = {}) => {
  const guard = (children) => (
    <RouteGuard role={role} route={view}>{children}</RouteGuard>
  );

  // SMART Build 2L — Launch Continuation. Mounted role-agnostically (NOT behind
  // RouteGuard) so the panel owns its exact role/tenant/age blocked copy instead
  // of the generic AccessDenied. The server callables re-enforce gp_employee +
  // active + same-tenant on every action.
  if (view === 'smart-continue') {
    return (
      <SmartLaunchContinueView
        launchSessionId={smartLaunchSessionId}
        user={user}
        onNavigate={handleNavigate}
      />
    );
  }

  // Settings is a universal personal/preferences page — handled role-agnostically
  // (like attachment-viewer) so every signed-in role reaches the same page.
  if (view === 'settings') {
    return guard(<SettingsView user={user} tenant={tenant} onNavigate={handleNavigate} onLogout={onLogout} />);
  }

  if (view === 'attachment-viewer') {
    return guard(
      <AttachmentPdfViewer onClose={() => {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.history.replaceState(null, '', '/');
          handleNavigate(defaultRouteForRole(role));
        }
      }} />
    );
  }

  if (isPlatformAdmin(role)) {
    switch (view) {
      case 'dashboard':        return guard(<PlatformDashboard onNavigate={handleNavigate} />);
      case 'tenants':          return guard(<TenantsView />);
      case 'users':            return guard(<UsersView activeFocusTarget={activeFocusTarget} />);
      case 'packs':            return guard(<PackManagerView />);
      case 'impact-settings':  return guard(<TenantImpactSettingsView activeFocusTarget={activeFocusTarget} onNavigate={handleNavigate} />);
      case 'ehr-integration':  return guard(<EhrIntegrationSettingsView />);
      case 'phi-policy':       return guard(<PhiPolicySettingsView />);
      case 'ops':              return guard(<OpsView />);
      case 'ops-home':         return guard(<AnalyticsGuard requiredView="OPS"><OpsHomeView onNavigate={handleNavigate} /></AnalyticsGuard>);
      case 'performance':      return guard(<AnalyticsGuard requiredView="PERFORMANCE"><PerformanceView /></AnalyticsGuard>);
      case 'deep-dive':        return guard(<AnalyticsGuard requiredView="DEEP_DIVE"><DeepDiveView /></AnalyticsGuard>);
      case 'flow-quality':     return guard(<FlowQualityFunnelView />);
      case 'ops-dashboard':    return guard(<PlatformOpsCommandCenter />);
      case 'packet-inbox':     return guard(<PacketInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:                 return <AccessDenied role={role} route={view} />;
    }
  }

  if (isTenantAdmin(role)) {
    switch (view) {
      case 'ops-dashboard': return guard(<OpsManagerDashboardView activeFocusTarget={activeFocusTarget} />);
      case 'audit-logs':    return guard(<AuditLogsView activeFocusTarget={activeFocusTarget} autoOpenReferralId={autoOpenReferralId} />);
      case 'users':         return guard(<UsersView activeFocusTarget={activeFocusTarget} />);
      // 'join-requests' was merged into Invites. Render InvitesView directly —
      // setView is NOT in scope inside this module-level renderView function, so
      // calling it here threw a ReferenceError if ever reached. (Route is not in
      // rbac getAllowedRoutes, so this case is currently unreachable, but kept
      // crash-safe in case it is wired up again.)
      case 'join-requests':
      case 'invites':       return guard(<InvitesView activeFocusTarget={activeFocusTarget} />);
      case 'referral-pings': return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'impact-settings':  return guard(<TenantImpactSettingsView activeFocusTarget={activeFocusTarget} onNavigate={handleNavigate} />);
      case 'ehr-integration':  return guard(<EhrIntegrationSettingsView />);
      case 'packet-inbox':  return guard(<PacketInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:              return <AccessDenied role={role} route={view} />;
    }
  }

  if (isOpsManager(role)) {
    switch (view) {
      case 'referral-pings':   return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'ops-dashboard':    return guard(<OpsManagerDashboardView activeFocusTarget={activeFocusTarget} />);
      case 'business-metrics': return guard(<TenantAdminBusinessMetrics />);
      case 'ops-home':         return guard(<AnalyticsGuard requiredView="OPS"><OpsHomeView onNavigate={handleNavigate} /></AnalyticsGuard>);
      case 'performance':      return guard(<AnalyticsGuard requiredView="PERFORMANCE"><PerformanceView /></AnalyticsGuard>);
      case 'deep-dive':        return guard(<AnalyticsGuard requiredView="DEEP_DIVE"><DeepDiveView /></AnalyticsGuard>);
      case 'flow-quality':     return guard(<FlowQualityFunnelView />);
      case 'workqueue':        return guard(<WorkqueueListView activeFocusTarget={activeFocusTarget} onModalContext={onModalContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'impact-settings':  return guard(<TenantImpactSettingsView activeFocusTarget={activeFocusTarget} onNavigate={handleNavigate} />);
      case 'packet-inbox':     return guard(<PacketInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:                 return <AccessDenied role={role} route={view} />;
    }
  }

  if (isGpEmployee(role)) {
    switch (view) {
      case 'dashboard':       return guard(<ClinicianDashboard user={user} onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'gate':            return guard(<GateView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} navState={navState} onLearningContext={onLearningContext} />);
      case 'not-yet-inbox':   return guard(<NotYetInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'referral-pings':  return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'my-submissions':  return guard(<MySubmissionsView autoOpenReferralId={autoOpenReferralId} activeFocusTarget={activeFocusTarget} onModalContext={onModalContext} />);
      case 'packet-inbox':    return guard(<PacketInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:                return <AccessDenied role={role} route={view} />;
    }
  }

  if (isIntakeCoordinator(role)) {
    switch (view) {
      case 'workqueue':    return guard(<WorkqueueListView activeFocusTarget={activeFocusTarget} onModalContext={onModalContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'not-yet-inbox':return guard(<NotYetInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'referral-pings': return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      // intake_coordinator may complete NOT_YET referrals via GateView (resume flow)
      case 'gate':         return guard(<GateView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} navState={navState} onLearningContext={onLearningContext} />);
      case 'ops-home':     return guard(<AnalyticsGuard requiredView="OPS"><OpsHomeView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} /></AnalyticsGuard>);
      case 'packet-inbox': return guard(<PacketInboxView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:             return <AccessDenied role={role} route={view} />;
    }
  }

  if (isTriageNurse(role)) {
    switch (view) {
      case 'referral-pings':      return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'triage-queue':        return guard(<TriageQueueView initialTab={navState?.tab} activeFocusTarget={activeFocusTarget} onModalContext={onModalContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'triage-queue-health': return guard(<TriageQueueHealthView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:                    return <AccessDenied role={role} route={view} />;
    }
  }

  if (isScheduler(role)) {
    switch (view) {
      case 'referral-pings':  return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'scheduling':      return guard(<SchedulingView activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'scheduler-home':  return guard(<SchedulerHomeView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      default:                return <AccessDenied role={role} route={view} />;
    }
  }

  if (isSpecialist(role)) {
    switch (view) {
      case 'referral-pings':          return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'specialist-worklist':     return guard(<SpecialistWorklistView initialTab={navState?.tab} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'packs':                   return guard(<SpecialistPackBuilderView activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} />);
      case 'specialist-queue-health': return guard(<SpecialistQueueHealthView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} />);
      default:                        return <AccessDenied role={role} route={view} />;
    }
  }

  if (isSpecialistAdmin(role)) {
    switch (view) {
      case 'referral-pings':                return guard(<ReferralPingsView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} />);
      case 'specialist-admin-workplace':    return guard(<SpecialistAdminWorkplaceView initialTab={navState?.tab} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} autoOpenReferralId={autoOpenReferralId} />);
      case 'specialist-admin-queue-health': return guard(<SpecialistAdminQueueHealthView onNavigate={handleNavigate} activeFocusTarget={activeFocusTarget} onLearningContext={onLearningContext} />);
      default:                              return <AccessDenied role={role} route={view} />;
    }
  }

  return <AccessDenied role={role} route={view} />;
};

// ============================================================================
// MAIN APP
// ============================================================================

export default function App() {
  // rawAuthUser: Firebase/demo auth user. Non-null means "signed in".
  // user: Firestore profile (/users/{uid}). Non-null means "profile loaded".
  const [rawAuthUser, setRawAuthUser] = useState(null);
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenants, setTenants] = useState({});
  const [viewerReturnCtx] = useState(() => consumeViewerReturnContext());
  const [view, setView] = useState(() => {
    if (window.location.pathname.startsWith('/attachments/view/')) return 'attachment-viewer';
    if (viewerReturnCtx?.referralId) return viewerReturnCtx.returnView || 'my-submissions';
    return 'dashboard';
  });

  // SMART Build 2L — capture a /ehr/smart/continue?launchSessionId=… deep link once.
  // Stash ONLY the launchSessionId (sessionStorage) and strip it from the URL so it
  // survives login and never lingers in the address bar. peek() also recovers an
  // intent stashed before login. Value: the id, 'INVALID' (bad/missing id), or null.
  const [smartContinuePending] = useState(() => {
    try {
      if (window.location.pathname === '/ehr/smart/continue') {
        const id = parseLaunchSessionIdFromSearch(window.location.search);
        if (id) setSmartContinueIntent(id);
        window.history.replaceState(null, '', '/');
        return id || 'INVALID';
      }
    } catch { /* ignore */ }
    return peekSmartContinueIntent();
  });
  const smartRoutedRef = useRef(false);
  const smartLaunchSessionId = smartContinuePending && smartContinuePending !== 'INVALID'
    ? smartContinuePending
    : null;

  // Auth bootstrap states
  const [authLoading, setAuthLoading] = useState(true);
  const [profileError, setProfileError] = useState(null); // null | 'permission_denied' | 'unknown'
  const [retrying, setRetrying] = useState(false);

  // Sidebar / mobile
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) !== 'false'; } catch { return true; }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewLearningCtx, setViewLearningCtx] = useState({});

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed)); } catch {}
  }, [sidebarCollapsed]);
  useEffect(() => { if (!isMobile) setMobileMenuOpen(false); }, [isMobile]);
  useEffect(() => { if (!isFirebase()) getTenants().then(setTenants); }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const applyProfile = useCallback(async (profile) => {
    // Heal isActive BEFORE setting user state or navigating.
    // If a user doc is missing isActive:true (created by old Cloud Function code),
    // all Firestore reads fail with PERMISSION_DENIED. We must stamp it first,
    // then proceed — otherwise every view loads and immediately errors out.
    if (isFirebase() && profile.isActive !== true) {
      try {
        await selfHealIsActiveCF();
      } catch {
        // If Functions aren't deployed yet, fall through — views will show
        // permission errors but the app won't crash.
      }
    }

    setUser(profile);
    setProfileError(null);
    if (!window.location.pathname.startsWith('/attachments/view/') && !viewerReturnCtx?.returnView) {
      setView(defaultRouteForRole(profile.role));
    }

    if (profile.tenantId) {
      const t = await getTenant(profile.tenantId).catch(() => null);
      setTenant(t);
    } else {
      setTenant(null);
    }
    if (isFirebase() && isPlatformAdmin(profile.role)) {
      getTenants().then(setTenants).catch(() => {});
    }
  }, []);

  const tryBootstrap = useCallback(async (authUser) => {
    if (!authUser) return;
    try {
      const { profile } = await ensureUserProfile(authUser);
      await applyProfile(profile);
    } catch (err) {
      setUser(null);
      setProfileError(
        err.code === 'PROFILE_PERMISSION_DENIED' ? 'permission_denied' : 'unknown'
      );
    }
  }, [applyProfile]);

  // ── Auth listener ───────────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthChange(async ({ user: authUser, profile }) => {
      setRawAuthUser(authUser);

      if (!authUser) {
        // Signed out
        setUser(null);
        setTenant(null);
        setProfileError(null);
        setAuthLoading(false);
        return;
      }

      if (profile) {
        // Auth adapter already loaded profile (demo mode, or Firebase with profile present)
        await applyProfile(profile);
      } else {
        // Firebase mode: profile not yet available — run bootstrap
        await tryBootstrap(authUser);
      }

      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [applyProfile, tryBootstrap]);

  // ── Auth actions ────────────────────────────────────────────────────────────

  const handleAuthSuccess = useCallback(async (profile) => {
    setRawAuthUser(profile); // mark signed-in (demo: profile IS the auth user)
    await applyProfile(profile);
  }, [applyProfile]);

  const handleLogout = useCallback(async () => {
    await logOut();
    setRawAuthUser(null);
    setUser(null);
    setTenant(null);
    setProfileError(null);
    setView('dashboard');
  }, []);

  // Apply the user's saved theme preference. Both themes are token-driven via
  // html[data-theme] (see src/styles/tokens.css). Mirror to localStorage so the
  // pre-paint script stays in sync on next load.
  //
  // Precedence:
  //   1. user.preferences.theme  (signed-in returning user — authoritative)
  //   2. localStorage.rg_theme   (last-known choice for unauth / pre-profile)
  //   3. "light"                  (default for fresh users / unauth screens)
  // Logout: user becomes null → falls through to localStorage so the last
  // explicit choice persists; never snaps to dark.
  useEffect(() => {
    const pref = user?.preferences?.theme;
    let stored = null;
    try { stored = localStorage.getItem('rg_theme'); } catch { /* ignore */ }
    const theme =
      pref === 'dark'   ? 'dark'  :
      pref === 'light'  ? 'light' :
      stored === 'dark' ? 'dark'  :
      'light';
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('rg_theme', theme); } catch { /* ignore */ }
  }, [user?.preferences?.theme]);

  const handleRetry = useCallback(async () => {
    if (!rawAuthUser) return;
    setRetrying(true);
    await tryBootstrap(rawAuthUser);
    setRetrying(false);
  }, [rawAuthUser, tryBootstrap]);

  // SMART Build 2L — once a profile is loaded, resume a pending launch continuation
  // by routing to the role-agnostic continuation view. applyProfile resets `view`
  // to the role's default on login, so this re-applies after. Runs at most once;
  // after that the user can navigate freely (and the view clears its own intent).
  useEffect(() => {
    if (smartRoutedRef.current) return;
    if (!user) return;
    if (!smartContinuePending) return;
    smartRoutedRef.current = true;
    setView('smart-continue');
  }, [user, smartContinuePending]);

  const [navState, setNavState] = useState(null);
  const handleNavigate = useCallback((v, state) => {
    if (user && canAccessRoute(user.role, v)) {
      // Reset per-view learning context on navigation. Stale modalContext /
      // viewLearningCtx from a drawer-heavy previous view (triage/specialist/
      // scheduler/admin) would otherwise make getLearningModuleForRoute take the
      // modal/context branch and return null for the destination route — hiding
      // the destination's route module (e.g. the Referral Pings module). The new
      // view re-establishes its own context on mount.
      setModalContext(null);
      setViewLearningCtx({});
      setNavState(state || null);
      setView(v);
    } else {
      console.warn(`[RouteGuard] Route "${v}" not allowed for role "${user?.role}"`);
    }
  }, [user]);

  // ── Role Orientation ─────────────────────────────────────────────────────────
  const role = user?.role;
  const orientationEnabled = !!user && !!role && isRoleLearningConfigured(role);
  const [showOrientationManual, setShowOrientationManual] = useState(false);
  const [orientationAutoShown, setOrientationAutoShown] = useState(false);
  const {
    loading: learningLoading,
    shouldAutoOpenOrientation,
    markOrientationAutoOpened,
    learningModeEnabled,
    completeRoleIntro,
    completeLearningStep,
    completeLearningModule,
    dismissLearningModule,
    undismissLearningModule,
    dismissForSession,
    isStepCompleted,
    isModuleCompleted,
    isModuleDismissedForSession,
  } = useRoleLearningState(
    orientationEnabled ? user.uid || user.id : null,
    orientationEnabled ? user.tenantId : null,
    orientationEnabled ? role : null,
  );
  const orientationVisible = orientationEnabled && (orientationAutoShown || showOrientationManual);

  const [modalContext, setModalContext] = useState(null);
  const activeModule = orientationEnabled && !orientationVisible
    ? getLearningModuleForRoute(role, '/' + view, modalContext, { ...viewLearningCtx, isModuleCompleted })
    : null;

  // First-login auto-open (Option A): when eligible and not yet shown this session,
  // show the orientation card AND persist the durable set-once marker. The local
  // `orientationAutoShown` keeps the card open even after the marker flips the rule
  // false, and prevents a same-session re-open loop even if the marker write fails.
  useEffect(() => {
    if (orientationEnabled && shouldAutoOpenOrientation && !orientationAutoShown && !showOrientationManual) {
      setOrientationAutoShown(true);
      markOrientationAutoOpened();
    }
  }, [orientationEnabled, shouldAutoOpenOrientation, orientationAutoShown, showOrientationManual, markOrientationAutoOpened]);
  const [manualLearningOpen, setManualLearningOpen] = useState(false);
  const [learningResetKey, setLearningResetKey] = useState(0);
  const activeModuleId = activeModule?.moduleId || null;
  useEffect(() => {
    if (activeModuleId) setLearningResetKey(k => k + 1);
  }, [activeModuleId]);
  // Settings → Preferences → Learning Guidance. When the user prefers Collapsed,
  // learning modules do not auto-open; the minimized toggle still appears and
  // manual open works. Legacy users (no preference) keep the expanded default.
  const learningDefaultCollapsed = user?.preferences?.learning?.defaultCollapsed === true;
  const showGuidedPanel = activeModule
    && !learningLoading
    && learningModeEnabled
    && !orientationVisible
    && (manualLearningOpen || (!learningDefaultCollapsed && !isModuleCompleted(activeModule.moduleId) && !isModuleDismissedForSession(activeModule.moduleId)));
  const showGuidedToggle = activeModule
    && !learningLoading
    && learningModeEnabled
    && !showGuidedPanel
    && !orientationVisible;

  const appNotYetCount = useNotYetBadgeCount({
    role,
    tenantId: tenant?.id || tenant?.tenantId || null,
    uid: user?.uid || user?.id || null,
  });
  const learningCtx = useMemo(() => ({ notYetCount: appNotYetCount ?? 0, role: user?.role ?? null, ...viewLearningCtx }), [appNotYetCount, user?.role, viewLearningCtx]);

  const [panelFocusTarget, setPanelFocusTarget] = useState(null);

  const activeFocusTarget = showGuidedPanel ? panelFocusTarget : null;

  // ── Render: accept-invite (standalone, no auth required to load) ────────────
  if (window.location.pathname === '/accept-invite') {
    return (
      <AcceptInviteView
        onAccepted={(role) => {
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }}
      />
    );
  }

  // ── Render: loading ─────────────────────────────────────────────────────────
  if (authLoading) return <LoadingScreen message="Signing you in…" />;

  // ── Render: not signed in ───────────────────────────────────────────────────
  if (!rawAuthUser && !user) {
    return <LoginView onAuthSuccess={handleAuthSuccess} tenants={tenants} />;
  }

  // ── Render: signed in but profile unreadable ────────────────────────────────
  if (rawAuthUser && !user && profileError) {
    return (
      <ProfileErrorView
        onLogout={handleLogout}
        onRetry={handleRetry}
        retrying={retrying}
      />
    );
  }

  // ── Render: signed in, bootstrap still running (edge case) ─────────────────
  if (rawAuthUser && !user) {
    return <LoadingScreen message="Loading profile…" />;
  }

  // ── Render: pending / unprovisioned ────────────────────────────────────────
  if (isPending(role) || (!user.tenantId && !isPlatformAdmin(role))) {
    return (
      <AuthContext.Provider value={{ user, tenant }}>
        <AccessPendingView user={user} onLogout={handleLogout} onRefresh={handleRetry} />
      </AuthContext.Provider>
    );
  }

  // ── Render: main app ────────────────────────────────────────────────────────
  return (
    <AuthContext.Provider value={{ user, tenant }}>
      <div className="min-h-screen flex relative">
        <AnimatedBackground />

        {isMobile && (
          <MobileHeader onOpenSidebar={() => setMobileMenuOpen(true)} role={role} />
        )}

        <Sidebar
          role={role}
          currentView={view}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          user={user}
          tenant={tenant}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
          isMobile={isMobile}
          isMobileOpen={mobileMenuOpen}
          onCloseMobile={() => setMobileMenuOpen(false)}
          activeFocusTarget={activeFocusTarget}
        />

        <main className={`
          flex-1 relative z-10 overflow-auto min-h-screen
          ${isMobile ? 'pt-14' : ''}
          ${!isMobile && sidebarCollapsed ? 'ml-16' : ''}
          ${!isMobile && !sidebarCollapsed ? 'ml-64' : ''}
          transition-all duration-300
        `}>
          {renderView(role, view, user, handleNavigate, { autoOpenReferralId: viewerReturnCtx?.referralId ?? navState?.autoOpenReferralId, navState, activeFocusTarget, onModalContext: setModalContext, onLearningContext: setViewLearningCtx, tenant, onLogout: handleLogout, smartLaunchSessionId })}
        </main>

        {orientationVisible && (
          <RoleOrientationCard
            orientationCard={getOrientationCard(role)}
            roleColor={getRoleColor(role)}
            onStart={async () => {
              await completeRoleIntro(view);
              setOrientationAutoShown(false);
              setShowOrientationManual(false);
            }}
            onDismiss={() => {
              dismissForSession();
              setOrientationAutoShown(false);
              setShowOrientationManual(false);
            }}
          />
        )}

        {/* Role Orientation has moved to Settings → Preferences; only the learning-
            guidance toggle remains here. The global "?" orientation button was removed. */}
        {!showGuidedPanel && !orientationVisible && showGuidedToggle && (
          <div className="fixed bottom-4 right-4 z-40 flex flex-col-reverse items-center gap-2">
            <button
              onClick={() => { undismissLearningModule(activeModule.moduleId); setManualLearningOpen(true); setLearningResetKey(k => k + 1); }}
              className={`
                w-10 h-10 rounded-full
                bg-slate-800/90 border border-${getRoleColor(role)}-500/30 backdrop-blur-sm
                flex items-center justify-center
                text-${getRoleColor(role)}-400 hover:bg-${getRoleColor(role)}-500/20 hover:border-${getRoleColor(role)}-500/50
                transition-colors duration-200 shadow-lg
              `}
              title="Show learning guidance"
            >
              <BookOpen className="w-4.5 h-4.5" />
            </button>
          </div>
        )}

        {showGuidedPanel && (
          <div className={`fixed bottom-4 z-40 max-w-sm w-full transition-all duration-300 ${viewLearningCtx.isResuming ? 'left-1/2 -translate-x-1/2' : modalContext ? 'right-[34rem]' : viewLearningCtx.specialistAdminClosingDrawer ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : viewLearningCtx.specialistUnreconciledDrawer ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : viewLearningCtx.specialistTodayDrawer ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : viewLearningCtx.specialistClosingDrawer ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : viewLearningCtx.schedulingDrawerOpen ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : viewLearningCtx.selectedReferralId ? (sidebarCollapsed ? 'left-20' : 'left-[17rem]') : 'right-4'}`}>
            <GuidedLearningPanel
              module={activeModule}
              roleColor={getRoleColor(role)}
              isStepCompleted={isStepCompleted}
              onCompleteStep={completeLearningStep}
              onCompleteModule={(moduleId) => { completeLearningModule(moduleId); setManualLearningOpen(false); }}
              onDismiss={(moduleId) => { dismissLearningModule(moduleId); setManualLearningOpen(false); }}
              learningCtx={learningCtx}
              onFocusTargetChange={setPanelFocusTarget}
              resetKey={learningResetKey}
            />
          </div>
        )}
      </div>
    </AuthContext.Provider>
  );
}
